import { useEffect, useState } from 'react'
import './index.css'

// ── Typewriter ──────────────────────────────────────────────────────────────
const TYPEWRITER_TEXTS = [
    'low-latency systems',
    'event-driven architectures',
    'AI platform infra',
    'systems that run at 2am',
    'distributed systems',
]

function useTypewriter(texts, speed = 90) {
    const [display, setDisplay] = useState('')
    const [idx, setIdx] = useState(0)
    const [charIdx, setCharIdx] = useState(0)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        const current = texts[idx]
        if (!deleting && charIdx === current.length) {
            const t = setTimeout(() => setDeleting(true), 2000)
            return () => clearTimeout(t)
        }
        if (deleting && charIdx === 0) {
            setDeleting(false)
            setIdx(i => (i + 1) % texts.length)
            return
        }
        const t = setTimeout(() => {
            setDisplay(current.slice(0, charIdx + (deleting ? -1 : 1)))
            setCharIdx(c => c + (deleting ? -1 : 1))
        }, deleting ? speed / 2 : speed)
        return () => clearTimeout(t)
    }, [charIdx, deleting, idx, texts, speed])

    return display
}

// ── EXPERIENCE DATA ─────────────────────────────────────────────────────────
const EXPERIENCE = [
    {
        company: 'ServiceNow',
        url: 'https://www.servicenow.com',
        role: 'Software Engineer II - AI Platform',
        period: 'Feb 2026 - Present',
        location: 'Hyderabad, India',
        bullets: [
            `Building backend services for AI Control Tower - the system that watches AI agents across the enterprise and keeps them in check.`,
            'Designed APIs to map AI assets to business services and infra dependencies in a multi-tenant SaaS environment. More interesting than it sounds.',
            `Rewrote the telemetry ingestion pipeline. It was dropping events under load. Now handles 1M+ events/day steadily - batching, async consumers, backpressure.`,
            'Found and fixed latency bottlenecks in 30+ enterprise AI workflows. Roughly 35% improvement in end-to-end response times.',
        ],
        tags: ['Java', 'Spring Boot', 'Kafka', 'SQL', 'Multi-Tenant SaaS', 'AI Governance'],
    },
    {
        company: 'Bank of New York (BNY)',
        url: 'https://www.bny.com',
        role: 'Full-Stack Software Developer II',
        period: 'Jul 2023 - Jan 2026',
        location: 'Pune, India',
        bullets: [
            'Led a 9-person team building the post-trade monitoring system - the one that alerts you when a large bank is about to overdraft. So it really has to work.',
            'Built a cash projection system that processes 2M+ trades every 15 minutes. Used to be a batch job. Turned it into a near-real-time pipeline.',
            'Optimised wire and SWIFT transaction ingestion (~10M transactions/day). Fixed a batch that was consistently slow and failing; cut processing time by 10s and failures by 30%.',
            'Set up CI/CD for 8+ repos with GitLab, Docker, and Kubernetes. Cut the release cycle from quarterly to bi-weekly.',
            'Built Angular micro-frontends for settlement and fund allocation across 1000+ international accounts.',
        ],
        tags: ['Java 17', 'Spring Boot', 'Kafka', 'Angular', 'Kubernetes', 'Reactive Programming', 'GC Tuning', 'Hazelcast'],
    },
]

// ── PROJECTS DATA ────────────────────────────────────────────────────────────
const PROJECTS = [
    {
        name: 'LettuceCache',
        desc: `A semantic caching layer that sits between your app and whatever LLM you're using. The idea is simple: why pay for the same API call twice? Redis handles exact matches in <30ms; FAISS handles 'close enough' via vector similarity. The tricky part was preventing a similar question in a different conversation context from returning a wrong cached answer. Solved that with a context signature.`,
        tags: ['C++17', 'Redis', 'FAISS', 'FastAPI', 'Docker', 'Kubernetes'],
        github: 'https://github.com/Ciphercrypt/LettuceCache',
        featured: true,
    },
    {
        name: 'Quasar - Traffic Management',
        desc: `Built a smart city traffic surveillance system with a YOLOv5 pipeline for real-time vehicle detection connected to a MERN dashboard. 90% accuracy on vehicle tracking. We presented it having slept maybe 4 hours total across the weekend.`,
        tags: ['Python', 'YOLOv5', 'React', 'Node.js', 'MongoDB', 'OpenCV'],
        github: 'https://github.com/Ciphercrypt/quassar',
        featured: true,
    },
    {
        name: 'Stree - AI Chatbot',
        desc: `Morgan Stanley Code-to-Give finalist. Multi-channel conversational AI for rural women entrepreneurs in India - web bot, voice telephony for feature phones, 3D avatar, and social media bot. The telephony piece was the most interesting challenge; most users didn't have smartphones.`,
        tags: ['Dialogflow', 'Flask', 'React', 'Twilio', 'NLP'],
        github: 'https://github.com/Ciphercrypt/MS-code-to-give',
        featured: false,
    },
    {
        name: 'Reddit Sarcasm Detection',
        desc: `Deep learning model that detects sarcasm in Reddit comments. Compared logistic regression, LSTM, and CNN approaches. LSTM won. The dataset had some genuinely confusing examples that tripped up all three models equally.`,
        tags: ['Python', 'TensorFlow', 'LSTM', 'NLP', 'Jupyter'],
        github: 'https://github.com/Ciphercrypt/Reddit-comments-sarcasm-detection-using-deep-learning',
        featured: false,
    },
    {
        name: 'Chat App Frontend',
        desc: `WhatsApp-style web chat UI built with Next.js and TypeScript. Clean component architecture, TailwindCSS for styling. An exercise in building something I'd actually want to use.`,
        tags: ['Next.js', 'TypeScript', 'TailwindCSS'],
        github: 'https://github.com/Ciphercrypt/chat-application-frontend',
        featured: false,
    },
]

// ── SKILLS DATA ──────────────────────────────────────────────────────────────
const SKILLS = [
    {
        category: 'Languages',
        items: ['Java 17+', 'Python', 'TypeScript', 'JavaScript', 'SQL', 'C++'],
    },
    {
        category: 'Backend',
        items: ['Spring Boot', 'Spring WebFlux', 'Kafka', 'Redis', 'RabbitMQ', 'Hazelcast', 'Hibernate / JPA', 'Spring Security', 'REST APIs'],
    },
    {
        category: 'Frontend',
        items: ['Angular', 'React', 'Next.js', 'HTML / CSS', 'Bootstrap'],
    },
    {
        category: 'Infrastructure',
        items: ['Docker', 'Kubernetes', 'AWS', 'S3', 'GitLab CI/CD', 'SonarQube', 'Splunk'],
    },
    {
        category: 'Databases',
        items: ['PostgreSQL', 'MongoDB', 'MySQL', 'Redis'],
    },
    {
        category: 'Performance & JVM',
        items: ['GC Tuning', 'JVM Internals', 'Multithreading', 'Non-Blocking I/O', 'Reactive Programming', 'Latency Profiling', 'Lock-Free Structures'],
    },
]

const ACHIEVEMENTS = [
    { icon: '🥈', text: 'Finalist - Morgan Stanley Code-to-Give (AI chatbot for rural entrepreneurs)' },
    { icon: '⭐', text: 'CodeChef 2003 peak · Top 10% LeetCode globally · 1200+ problems solved' },
    { icon: '📈', text: 'Top 5% individual contributor at BNY by code contribution metrics' },
    { icon: '👨‍💼', text: 'Youngest development lead in my org at BNY' },
]

// ── HOME ─────────────────────────────────────────────────────────────────────
const Home = () => {
    const typeText = useTypewriter(TYPEWRITER_TEXTS)

    return (
        <>
            {/* ── HERO ──────────────────────────────────────────────────────── */}
            <section className="hero" id="hero">
                <div className="container">
                    <div className="hero__content">
                        <p className="hero__greeting reveal">hi, my name is</p>
                        <h1 className="hero__name reveal reveal-delay-1">Avishkar Mohite.</h1>
                        <h2 className="hero__tagline reveal reveal-delay-2">
                            I build things that run fast.
                        </h2>
                        <p className="hero__desc reveal reveal-delay-3">
                            Software engineer at{'  '}
                            <a href="https://www.servicenow.com" target="_blank" rel="noreferrer" className="hero__company">
                                ServiceNow
                            </a>
                            {'  '}working on AI platform infrastructure.
                            Before that, I spent a couple of years at BNY making sure millions of
                            financial transactions settled correctly every day, without anyone noticing.
                        </p>
                        <div className="hero__cta reveal reveal-delay-3">
                            <a href="#projects" className="btn btn--primary">see my work</a>
                            <a href="#contact" className="btn btn--outline">get in touch</a>
                        </div>
                        <div className="hero__terminal reveal reveal-delay-3">
                            <span className="hero__terminal-prompt">~/avishkar $ </span>
                            <span className="hero__terminal-text">working on </span>
                            <span className="hero__terminal-typed">{typeText}</span>
                            <span className="hero__cursor">▋</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── ABOUT ─────────────────────────────────────────────────────── */}
            <section className="section about" id="about">
                <div className="container">
                    <div className="section-heading reveal">
                        <p className="section-label">01. about</p>
                        <h2>about me</h2>
                        <div className="section-heading-line" />
                    </div>

                    <div className="about__grid">
                        <div className="about__text">
                            <p className="reveal">
                                I'm a software engineer based in India. I like making systems fast - like, really fast.
                                The kind of fast where nothing seems to be happening, but the numbers tell a different story.
                            </p>
                            <p className="reveal reveal-delay-1">
                                Over two years at BNY, I helped build the backend that keeps post-trade processing
                                running smoothly for one of the world's largest custodian banks. That meant deep
                                dives into JVM internals, Kafka partitioning strategies, and figuring out exactly
                                which SQL index would turn a 40-second query into a 40-millisecond one.
                            </p>
                            <p className="reveal reveal-delay-2">
                                These days I'm at ServiceNow, working on the infrastructure layer for their AI
                                platform. Different problem space, same obsession with performance. Outside of work
                                I also do competitive programming - solved 1200+ problems, peaked at CodeChef 2003.
                                I like having a problem where there is one correct answer.
                            </p>

                            <div className="about__currently reveal reveal-delay-2">
                                <p className="about__currently-label">some things I've been working with lately:</p>
                                <div className="about__chips">
                                    {['Java 17+', 'Spring Boot', 'Kafka', 'Redis', 'Kubernetes', 'TypeScript', 'Python', 'AWS'].map(t => (
                                        <span key={t} className="chip">{t}</span>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Terminal-style profile card */}
                        <div className="about__card reveal reveal-delay-1">
                            <div className="about__card-bar">
                                <span className="about__card-dot about__card-dot--red" />
                                <span className="about__card-dot about__card-dot--yellow" />
                                <span className="about__card-dot about__card-dot--green" />
                                <span className="about__card-filename">avishkar.json</span>
                            </div>
                            <div className="about__card-body">
                                <div className="about__card-row">
                                    <span className="about__card-key">"name"</span>
                                    <span className="about__card-sep">:</span>
                                    <span className="about__card-str">"Avishkar Mohite"</span>
                                </div>
                                <div className="about__card-row">
                                    <span className="about__card-key">"role"</span>
                                    <span className="about__card-sep">:</span>
                                    <span className="about__card-str">"Software Engineer"</span>
                                </div>
                                <div className="about__card-row">
                                    <span className="about__card-key">"at"</span>
                                    <span className="about__card-sep">:</span>
                                    <span className="about__card-str about__card-str--accent">"ServiceNow"</span>
                                </div>
                                <div className="about__card-row">
                                    <span className="about__card-key">"based"</span>
                                    <span className="about__card-sep">:</span>
                                    <span className="about__card-str">"Pune, India"</span>
                                </div>
                                <div className="about__card-row">
                                    <span className="about__card-key">"edu"</span>
                                    <span className="about__card-sep">:</span>
                                    <span className="about__card-str">"B.Tech CSE, VJTI"</span>
                                </div>
                                <div className="about__card-row">
                                    <span className="about__card-key">"focus"</span>
                                    <span className="about__card-sep">:</span>
                                    <span className="about__card-str">"backend · performance"</span>
                                </div>
                                <div className="about__card-row">
                                    <span className="about__card-key">"open_to"</span>
                                    <span className="about__card-sep">:</span>
                                    <span className="about__card-str">"interesting problems"</span>
                                </div>
                            </div>
                            <div className="about__stats">
                                <div className="stat">
                                    <span className="stat__num">2.5+</span>
                                    <span className="stat__label">years exp</span>
                                </div>
                                <div className="stat">
                                    <span className="stat__num">1200+</span>
                                    <span className="stat__label">problems solved</span>
                                </div>
                                <div className="stat">
                                    <span className="stat__num">2003</span>
                                    <span className="stat__label">CodeChef peak</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── EXPERIENCE ────────────────────────────────────────────────── */}
            <section className="section experience" id="experience">
                <div className="container">
                    <div className="section-heading reveal">
                        <p className="section-label">02. experience</p>
                        <h2>where I've worked</h2>
                        <div className="section-heading-line" />
                    </div>

                    <div className="exp__timeline">
                        {EXPERIENCE.map((job, i) => (
                            <div key={job.company} className="exp__item reveal" style={{ transitionDelay: `${i * 0.1}s` }}>
                                <div className="exp__item-marker">
                                    <div className="exp__item-dot" />
                                    {i < EXPERIENCE.length - 1 && <div className="exp__item-line" />}
                                </div>
                                <div className="exp__item-content">
                                    <div className="exp__item-header">
                                        <div className="exp__item-title-group">
                                            <h3 className="exp__item-role">{job.role}</h3>
                                            <a
                                                href={job.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="exp__item-company"
                                            >
                                                @ {job.company}
                                            </a>
                                        </div>
                                        <div className="exp__item-meta">
                                            <span className="exp__item-period">{job.period}</span>
                                            <span className="exp__item-location">{job.location}</span>
                                        </div>
                                    </div>
                                    <ul className="exp__bullets">
                                        {job.bullets.map((b, j) => (
                                            <li key={j}>{b}</li>
                                        ))}
                                    </ul>
                                    <div className="exp__tags">
                                        {job.tags.map(t => (
                                            <span key={t} className="chip chip--small">{t}</span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── PROJECTS ──────────────────────────────────────────────────── */}
            <section className="section projects" id="projects">
                <div className="container">
                    <div className="section-heading reveal">
                        <p className="section-label">03. projects</p>
                        <h2>things I've built</h2>
                        <div className="section-heading-line" />
                    </div>

                    <div className="projects__featured">
                        {PROJECTS.filter(p => p.featured).map((proj, i) => (
                            <div
                                key={proj.name}
                                className="project-card project-card--featured reveal"
                                style={{ transitionDelay: `${i * 0.1}s` }}
                            >
                                <div className="project-card__content">
                                    <p className="project-card__label">featured project</p>
                                    <h3 className="project-card__name">{proj.name}</h3>
                                    <div className="project-card__desc">
                                        <p>{proj.desc}</p>
                                    </div>
                                    <ul className="project-card__tags">
                                        {proj.tags.map(t => <li key={t}><span className="chip chip--mono">{t}</span></li>)}
                                    </ul>
                                    <div className="project-card__links">
                                        <a href={proj.github} target="_blank" rel="noreferrer" aria-label="GitHub">
                                            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/></svg>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <h3 className="projects__other-heading reveal">other stuff</h3>
                    <div className="projects__grid">
                        {PROJECTS.filter(p => !p.featured).map((proj, i) => (
                            <a
                                key={proj.name}
                                href={proj.github}
                                target="_blank"
                                rel="noreferrer"
                                className="project-small reveal"
                                style={{ transitionDelay: `${i * 0.08}s` }}
                            >
                                <div className="project-small__top">
                                    <svg className="project-small__folder" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                                    <svg title="View on GitHub" className="project-small__github" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/></svg>
                                </div>
                                <h4 className="project-small__name">{proj.name}</h4>
                                <p className="project-small__desc">{proj.desc.split('.')[0]}.</p>
                                <ul className="project-small__tags">
                                    {proj.tags.slice(0, 3).map(t => <li key={t}>{t}</li>)}
                                </ul>
                            </a>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── SKILLS ────────────────────────────────────────────────────── */}
            <section className="section skills" id="skills">
                <div className="container">
                    <div className="section-heading reveal">
                        <p className="section-label">04. skills</p>
                        <h2>the toolbox</h2>
                        <div className="section-heading-line" />
                    </div>

                    <div className="skills__grid">
                        {SKILLS.map((group, i) => (
                            <div key={group.category} className="skill-group reveal" style={{ transitionDelay: `${i * 0.07}s` }}>
                                <h3 className="skill-group__title">{group.category}</h3>
                                <div className="skill-group__chips">
                                    {group.items.map(item => (
                                        <span key={item} className="chip">{item}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="achievements reveal">
                        <h3 className="achievements__heading">a few things I'm proud of</h3>
                        <div className="achievements__grid">
                            {ACHIEVEMENTS.map(({ icon, text }) => (
                                <div key={text} className="achievement">
                                    <span className="achievement__icon">{icon}</span>
                                    <span className="achievement__text">{text}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CONTACT ───────────────────────────────────────────────────── */}
            <section className="section contact" id="contact">
                <div className="container">
                    <div className="contact__inner">
                        <p className="section-label reveal">05. contact</p>
                        <h2 className="contact__heading reveal">get in touch</h2>
                        <p className="contact__desc reveal">
                            Whether it's a job, a project, a random idea, or just want to talk
                            about distributed systems and performance - my inbox is open.
                            I try to reply to everyone.
                        </p>
                        <a
                            href="mailto:avishkar.mohite.eng@gmail.com"
                            className="btn btn--primary reveal"
                        >
                            say hello →
                        </a>
                        <div className="contact__social reveal">
                            <a href="https://github.com/Ciphercrypt" target="_blank" rel="noreferrer">GitHub</a>
                            <span>·</span>
                            <a href="https://linkedin.com/in/avishkar-mohite" target="_blank" rel="noreferrer">LinkedIn</a>
                            <span>·</span>
                            <a href="https://www.codechef.com/users/ciphercrypt" target="_blank" rel="noreferrer">CodeChef</a>
                            <span>·</span>
                            <a href="https://leetcode.com/ciphercrypt" target="_blank" rel="noreferrer">LeetCode</a>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}

export default Home
